import React from 'react';
import { Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import toastLogoUrl from '@/assets/images/icons/alpha_toast_logo.svg?url';
import { getCoinName } from '@/lib/helpers/hypertrackerHelpers';
import { getCoinIconBgColor, getCoinIconUrl } from '@/pages/HypertrackerAlphafeed/alphafeedHelpers';
import {
    ToastCloseButton,
    ToastCloseIcon,
    ToastCoinIcon,
    ToastErrorIcon,
    ToastIconSlot,
    ToastRoot,
    ToastText,
} from './AlphaFeedToastStyled';

export type AlphaFeedToastVariant = 'error' | 'processing' | 'info';

interface AlphaFeedToastProps {
    variant: AlphaFeedToastVariant;
    title: string;
    subtitle?: string;
    coin?: string;
    onClose: () => void;
}

const AlphaFeedToast: React.FC<AlphaFeedToastProps> = ({
    variant,
    title,
    subtitle,
    coin,
    onClose,
}) => {
    const { primitives } = useTheme().palette;
    const { t } = useTranslation('alphafeed', { keyPrefix: 'trade' });

    const renderIcon = () => {
        if (variant === 'error') return <ToastErrorIcon />;
        if (variant === 'processing') {
            return (
                <ToastIconSlot>
                    <img src={toastLogoUrl} width={18} height={23.29} alt={''} />
                </ToastIconSlot>
            );
        }
        if (!coin) return null;
        const coinName = getCoinName(coin);
        return (
            <ToastCoinIcon
                src={getCoinIconUrl(coin)}
                alt={coinName}
                sx={{ bgcolor: getCoinIconBgColor(coin) }}
            >
                {coinName[0]?.toUpperCase()}
            </ToastCoinIcon>
        );
    };

    const isError = variant === 'error';
    const titleColor = {
        error: primitives.base.white,
        processing: primitives.woodsmoke[400],
        info: primitives.woodsmoke[50],
    }[variant];

    return (
        <ToastRoot variant={variant} role={isError ? 'alert' : 'status'}>
            {renderIcon()}
            <ToastText>
                <Typography
                    variant={isError ? 'p14' : 'p12'}
                    fontWeight={variant === 'processing' ? 500 : 600}
                    lineHeight={1.33}
                    color={titleColor}
                    sx={{ fontFeatureSettings: '"lnum" 1, "tnum" 1' }}
                >
                    {title}
                </Typography>
                {subtitle && (
                    <Typography
                        variant={'p12'}
                        fontWeight={400}
                        lineHeight={'normal'}
                        color={primitives.woodsmoke[400]}
                    >
                        {subtitle}
                    </Typography>
                )}
            </ToastText>
            <ToastCloseButton onClick={onClose} aria-label={t('errors.close')}>
                <ToastCloseIcon />
            </ToastCloseButton>
        </ToastRoot>
    );
};

export default AlphaFeedToast;
